// VueRouter imports
import {createRouter, createWebHistory} from 'vue-router';

// Route component
import DashboardView from '../views/DashboardView.vue';
import ExternalMapView from '../views/ExternalMapView.vue';
import Error404Page from '../views/errorPages/Error404Page.vue';

// Services
import {auth, oAuthSignIn, currentUser, getDocument} from '@/services/FirestoreService';
import {getStore} from '@/services/store';

const tryAuthentication = async () => {
  try {
    const user = (await oAuthSignIn('microsoft.com', {tenant: '1b851592-2520-4963-88ae-60fa1ed11758'})).user;
    return user;
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user') {
      console.error('User closed the popup', error);
      return false;
    }
    if (error.code === 'auth/popup-blocked') {
      console.error('The popup was blocked, you must allow popups to sign in.');
      throw new Error('Please allow popups to sign in.');
    }
    console.error('Error authenticating user:', error);
    // If the error isn't one of these expected types, it's probably pretty serious.
    throw error;
  }
};

const userCanEditMap = (map, uid) => {
  return map.createdOn?.uid === uid || map.share?.accessLevel === 'edit' || (map.share?.users?.includes(uid) ?? false);
};

const mapIsPublic = (map) => {
  return map && !map.deleted && map.share?.accessType === 'link';
};

/** @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw */
/** @type {RouteRecordRaw[]} */
const routes = [
  {
    path: '/',
    name: 'DashboardView',
    component: DashboardView,
    beforeEnter: async (to, from) => {
      // User must sign in when accessing the home dashboard, if not already signed in
      if (currentUser()?.uid) return true;

      try {
        if (await tryAuthentication()) return true;
      } catch (error) {
        getStore().dispatch('addErrorMessage', `Unable to sign in: ${error}`);
        // TODO: replace this with a 403 page
        return false;
      }
      console.error('Unable to sign in.');
      getStore().dispatch('addErrorMessage', 'You must sign in to access this page. Refresh the page and enter your credentials when prompted.');
      // TODO: replace this with a 403 page
      return false;
    },
  },
  { // Redirect to view mode if the url is missing the mode parameter
    path: '/:id',
    name: 'ExternalMapViewRedirect',
    beforeEnter: async (to) => {
      const mapId = to.params.id;
      return {name: 'ExternalMapView', params: {id: mapId, mode: 'view'}};
    },
  },
  {
    path: `/:id/:mode`,
    name: 'ExternalMapView',
    component: ExternalMapView,
    beforeEnter: async (to, from) => {
      const mapId = to.params.id;
      const mode = to.params.mode;
      // If the user is redirected from edit to view mode, skip the login popup
      const skipPopUp = to.redirectedFrom?.path === `/${mapId}/edit`;

      // Attempt to retrieve the map.
      // If the mode is view and the map is accessible to anyone with the link, continue to view mode
      let map;
      let user = currentUser();
      try {
        map = await getDocument('sharedMaps', mapId);
        if (mode === 'view' && mapIsPublic(map)) {
          // The user is authorized if they are redirected from edit to view mode
          await getStore().dispatch('setupMap', {mode, id: mapId, map, isAuthorizedUser: user?.uid !== undefined});
          return true;
        }
      } catch (error) {
        // This might fail due to security rules, so continue on failure
        // and give the user an opportunity to authenticate
        console.error('Unable to get map:', error);
      }

      // Attempt to authenticate the user before retrieving the map
      if (!user && !skipPopUp) {
        try {
          user = await tryAuthentication();
        } catch (error) {
          getStore().dispatch('addErrorMessage', `Unable to sign in: ${error}`);
        }
      }

      const isAuthorizedUser = user?.uid !== undefined;
      if (!map) {
        try {
          // Queries to make sure the map exists, is not deleted, and the user has permission to access it
          map = await getDocument('sharedMaps', mapId);
        } catch (error) {
          console.error('Unable to get map:', error);
          getStore().dispatch('addErrorMessage', error);
          return {name: 'Error404Page'};
        }
      }

      if (!map) {
        console.error(`Map ${mapId} does not exist in Firestore collection sharedMaps`);
        if (currentUser()?.uid) {
          getStore().dispatch('addErrorMessage', 'Map does not exist');
          return {name: 'DashboardView'};
        }
        return {name: 'Error404Page'};
      }

      if (map.deleted) {
        console.error(`Map ${mapId} was deleted`);
        if (currentUser()?.uid) {
          getStore().dispatch('addErrorMessage', `Map was deleted`);
          return {name: 'DashboardView'};
        }
        return {name: 'Error404Page'};
      }

      if (mode === 'view') {
        await getStore().dispatch('setupMap', {mode, id: mapId, map, isAuthorizedUser});
        return true;
      }

      if (user?.uid) {
        if (!userCanEditMap(map, user?.uid)) {
          // Redirect to view mode if user is not allowed to edit the map
          getStore().commit('setSnack', {message: 'You do not have permissions to edit this map. Opening map in View mode.', color: 'error'});
          return {name: 'ExternalMapView', params: {id: mapId, mode: 'view'}};
        }
        await getStore().dispatch('setupMap', {mode, id: mapId, map, isAuthorizedUser});
        return true;
      } else if (map.share?.accessType === 'link') {
        // Redirect to view mode if the map is accessible to anyone with the link
        getStore().commit('setSnack', {message: 'You must sign in to edit maps. Opening map in View mode.', color: 'error'});
        return {name: 'ExternalMapView', params: {id: mapId, mode: 'view'}};
      }

      console.error('Unable to access map.');
      return {name: 'Error404Page'};
    },
  },
  {
    path: '/404',
    name: 'Error404Page',
    component: Error404Page,
  },
];

const router = createRouter({
  base: process.env.VUE_APP_PUBLIC_PATH,
  history: createWebHistory(process.env.VUE_APP_PUBLIC_PATH),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return {top: 0};
    }
  },
});

router.beforeEach(async (to, from) => {
  await auth.authStateReady();
  return true;
});

router.onError((error) => {
  console.error('router.onError:', error);
});

export {routes};
export default router;
